import moment from "moment";
import { useEffect, useState } from "react";
import { SmartSoftButton } from "soft_digi";
import { METER_READINGS_URLS } from "../../api/UserUrls";
import { useSiteContext } from "../../contexts/SiteProvider";
import { post } from "../../services/smartApiService";
import MeterReadingForm from "./MeterReadingForm";
import ImportReportTable from "./ImportReportTable";

const MONTHS = [
  "January", "February", "March", "April",
  "May", "June", "July", "August",
  "September", "October", "November", "December",
];

const MeterReadingReport = () => {
  const { openModal } = useSiteContext();

  const [data, setData]           = useState<any[]>([]);
  const [startYear, setStartYear] = useState(moment().year());
  const [searchTerm, setSearchTerm] = useState<string>("");

  // ── Data ─────────────────────────────────────────────────────
  const loadData = () => {
    const subscription = post(METER_READINGS_URLS.GET_ALL, { year: startYear }).subscribe(
      (response) => setData(response.data.data)
    );
    return () => subscription.unsubscribe();
  };

  useEffect(() => { loadData(); }, [startYear]);

  const filteredData = data.filter(
    (item) =>
      item.hub_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.office_city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Month cell lookup ────────────────────────────────────────
  const getDayobj = (month: string, subData: any[]) => {
    if (subData?.length > 0) {
      return subData.find((obj) => obj.month === month) ?? null;
    }
    return null;
  };

  // ── Modal openers ────────────────────────────────────────────
  const openMeterForm = (dataIn?: any) => {
    openModal({
      title: <>{dataIn?.ID ? "Edit Meter Reading" : "Add Meter Reading"}</>,
      content: <MeterReadingForm dataIn={dataIn} loadTableData={loadData} />,
      width: 40,
      className: "sd-efl-modal",
      closeBody: false,
    });
  };

  /** Open form pre-filled with the hub from this row and today as start date */
  const openHubMeterForm = (hub: any) => {
    openMeterForm({
      sd_hub_id: { value: hub.ID, label: hub.hub_name },
      meter_start_date: moment().format("YYYY-MM-DD"),
      meter_end_date: "",
      meter_start: 0,
      meter_end: 0,
      numberof_unit: 0,
      meter_cost: 0,
    });
  };

  /** Open form pre-filled with existing reading data */
  const viewEditForm = (id: any) => {
    const subscription = post(METER_READINGS_URLS.GET_ONE, { id }).subscribe(
      (response: any) => openMeterForm(response.data)
    );
    return () => subscription.unsubscribe();
  };

  const openImportForm = () => {
    openModal({
      title: "Import Data",
      content: <ImportReportTable loadTableData={loadData} />,
      className: "sd-efl-modal",
      closeBody: false,
    });
  };

  // ── Render ───────────────────────────────────────────────────
  return (
    <div className="efl-report-card">

      {/* ── Header row ── */}
      <div className="efl-report-header-row columns is-vcentered is-multiline">
        <div className="column is-narrow">
          <span className="efl-report-title">Meter Reading Report</span>
        </div>

        <div className="column">
          <div className="is-flex is-align-items-center" style={{ gap: "10px" }}>
            {/* Search */}
            <div className="search-box sd-efl-input">
              <input
                className="input"
                type="text"
                placeholder="Search hub or city…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Import */}
            <span
              className="has-text-link is-clickable"
              title="Import"
              onClick={openImportForm}
            >
              <i className="fa fa-download is-size-4" />
            </span>
          </div>
        </div>

        {/* Year navigation + Add button */}
        <div className="column is-narrow">
          <div className="is-flex is-align-items-center" style={{ gap: "10px" }}>
            <div className="date-navigation efl-year-nav">
              <span className="icon is-clickable" onClick={() => setStartYear((y) => y - 1)}>
                <i className="fa fa-arrow-left" />
              </span>
              <span className="efl-year-label">{startYear}</span>
              <span className="icon is-clickable" onClick={() => setStartYear((y) => y + 1)}>
                <i className="fa fa-arrow-right" />
              </span>
            </div>

            <SmartSoftButton
              label="Add"
              onClick={() => openMeterForm(undefined)}
              leftIcon="fa fa-plus"
              classList={["button", "is-link", "is-small", "px-4"]}
            />
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="scrollable-table">
        <table className="table is-bordered is-fullwidth smart-report-table">
          <thead>
            <tr>
              <th>Hub Name</th>
              {MONTHS.map((m) => <th key={m}>{m}</th>)}
            </tr>
          </thead>

          <tbody>
            {filteredData.map((hub) => (
              <tr key={hub.hub_name}>

                {/* ── Hub Name cell with inline + button ── */}
                <td>
                  <div className="efl-meter-hub-cell">
                    <div className="efl-meter-hub-info">
                      <span className="efl-hub-name">{hub.hub_name}</span>
                      <span className="efl-hub-city">({hub.office_city})</span>
                    </div>

                    {/* ── Add reading for this hub ── */}
                    <button
                      className="efl-hub-add-btn"
                      title={`Add reading for ${hub.hub_name}`}
                      onClick={() => openHubMeterForm(hub)}
                    >
                      <i className="fa fa-plus" />
                    </button>
                  </div>
                </td>

                {/* ── Month columns ── */}
                {MONTHS.map((month, idx) => {
                  const entry = getDayobj(month, hub.meter_data);

                  return entry && entry.meter_reading ? (
                    <td key={idx} className="has-text-centered">
                      <span
                        className="efl-meter-entry sd-cursor"
                        onClick={() => viewEditForm(entry.ID)}
                      >
                        <span className="efl-meter-reading">{entry.meter_reading}</span>
                        <span className="efl-meter-cms">{entry.cms_reading}</span>
                        <hr className="efl-meter-divider" />
                        <span
                          className="efl-meter-deviation"
                          style={{ color: entry.deviation > 10 ? "#e53e3e" : "#38a169" }}
                        >
                          {entry.deviation}
                        </span>
                      </span>
                    </td>
                  ) : (
                    <td key={idx} />
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MeterReadingReport;
